import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Quote } from "lucide-react";
import { Reveal, Overline } from "@/components/ui-bits";
import { stagger, fadeUp, viewport } from "@/lib/motion";

function CountUp({ value, prefix = "", suffix = "", decimals = 0 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const duration = 1600;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value]);

  return (
    <span ref={ref}>
      {prefix}
      {display.toFixed(decimals)}
      {suffix}
    </span>
  );
}

const STATS = [
  { node: <CountUp value={4.2} prefix="$" suffix="M+" decimals={1} />, label: "Investment Value Protected" },
  { node: <CountUp value={50} suffix="+" />, label: "Investigations Completed" },
  { node: "7–21", label: "Day Turnaround" },
  { node: <CountUp value={100} suffix="%" />, label: "Independent & Confidential" },
];

const TESTIMONIALS = [
  {
    tag: "Investment · London",
    quote:
      "Something about this deal rubbed me the wrong way and I couldn't shake that feeling. But I loved the property and didn't want to lose it. I was introduced to BAT Ear Research. In 7 days, Foresight returned clear answers that saved me £150,000. Anyone looking to invest in Ghana — don't do it without talking to BAT Ear Research first.",
    author: "M.K., Diaspora Investor, London",
  },
  {
    tag: "Citizenship · Texas",
    quote:
      "BAT Ear helped me get my Ghanaian citizenship — it was such a smooth process. Having a Ghanaian be my voice made such a difference.",
    author: "Colette N., Texas",
  },
];

export default function CaseStudy() {
  return (
    <section
      id="case-study"
      className="relative bg-navy-deep grain py-24 sm:py-32 overflow-hidden"
      data-testid="case-study-section"
    >
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-3xl">
          <Reveal>
            <Overline index={8}>Case Studies</Overline>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="mt-6 font-display font-bold text-white leading-[1.05] tracking-tight text-3xl sm:text-4xl lg:text-5xl">
              Real Investigations. Real Results.
            </h2>
          </Reveal>
        </div>

        {/* Two testimonials */}
        <motion.div
          variants={stagger(0.15)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-14 grid lg:grid-cols-2 gap-6"
        >
          {TESTIMONIALS.map((t, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="relative bg-navy border border-gold/25 p-8 sm:p-10 flex flex-col"
              data-testid={`testimonial-${i + 1}`}
            >
              <Quote
                size={56}
                className="text-gold/25 absolute top-6 left-6"
                strokeWidth={1}
              />
              <div className="text-[11px] uppercase tracking-[0.28em] text-gold relative z-10">
                {t.tag}
              </div>
              <blockquote className="relative z-10 mt-5 font-display text-lg sm:text-xl leading-relaxed text-white/90 flex-1">
                “{t.quote}”
              </blockquote>
              <div className="mt-7 flex items-center gap-3">
                <span className="h-px w-8 bg-gold" />
                <span className="text-white/70 text-sm uppercase tracking-wider">
                  {t.author}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <motion.div
          variants={stagger(0.12)}
          initial="hidden"
          whileInView="visible"
          viewport={viewport}
          className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10"
        >
          {STATS.map((s, i) => (
            <motion.div
              key={i}
              variants={fadeUp}
              className="bg-navy-deep p-8 text-center"
              data-testid={`stat-${i + 1}`}
            >
              <div className="font-display text-3xl sm:text-5xl font-bold text-gold">
                {s.node}
              </div>
              <div className="mt-3 text-white/60 text-xs sm:text-sm uppercase tracking-wider">
                {s.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
